import {
  DEFAULT_BALANCE,
  FACILITY_BY_ID,
  RESOURCE_ABBR,
  RESOURCES,
  SEA_ZONE_BY_ID,
  TERRITORY_BY_ID,
  UNIT_BY_ID,
  type Balance,
  type ObjectiveDef,
  type ResourceBag,
  type ResourceId,
} from '@war-game/game-data';
import type { CombatStrike, MatchConfig, PlayerView, UnitCounts, VictoryMode } from '@war-game/engine';

const MONTHS = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** Grupo data-hora militar (DTG) com horário Zulu: 282247Z SET 26. */
export function dtg(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getUTCDate())}${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}Z ${MONTHS[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
}

export function hhmmZ(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}Z`;
}

/** Contagem regressiva: H−04:12:33. */
export function countdown(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `H−${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export function duration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  return `${h}h${String(m % 60).padStart(2, '0')}`;
}

const nf = new Intl.NumberFormat('pt-BR');
export function num(n: number): string {
  return nf.format(Math.round(n));
}

export function pct(x: number): string {
  return `${Math.round(x * 100)}%`;
}

const COUNTRY_CODES: Record<string, string> = {
  alasca: 'AK',
  groenlandia: 'GL',
  caribe: 'CAR',
  'america-central': 'AMC',
  colombia: 'CO',
  venezuela: 'VE',
  guianas: 'GU',
  equador: 'EC',
  peru: 'PE',
  bolivia: 'BO',
  chile: 'CL',
  paraguai: 'PY',
  uruguai: 'UY',
  islandia: 'IS',
  irlanda: 'IE',
  'reino-unido': 'UK',
  noruega: 'NO',
  suecia: 'SE',
  finlandia: 'FI',
  portugal: 'PT',
  espanha: 'ES',
  franca: 'FR',
  benelux: 'BNL',
  alemanha: 'DE',
  alpes: 'ALP',
  italia: 'IT',
  polonia: 'PL',
  'europa-central': 'EUC',
  balcas: 'BAL',
  grecia: 'GR',
  romenia: 'RO',
  balticos: 'BLT',
  belarus: 'BY',
  ucrania: 'UA',
  marrocos: 'MA',
  argelia: 'DZ',
  tunisia: 'TN',
  libia: 'LY',
  egito: 'EG',
  sudao: 'SD',
  etiopia: 'ET',
  'chifre-da-africa': 'CHF',
  'sahel-ocidental': 'SHL',
  chade: 'TD',
  'africa-ocidental': 'AOC',
  'golfo-da-guine': 'GGN',
  nigeria: 'NG',
  camaroes: 'CM',
  gabao: 'GA',
  congo: 'CG',
  'africa-oriental': 'AOR',
  angola: 'AO',
  zambia: 'ZM',
  mocambique: 'MZ',
  namibia: 'NA',
  'africa-do-sul': 'ZA',
  madagascar: 'MG',
  turquia: 'TR',
  caucaso: 'CAU',
  levante: 'LEV',
  iraque: 'IQ',
  'arabia-saudita': 'SA',
  'iemen-oma': 'YE',
  'estados-do-golfo': 'GLF',
  ira: 'IR',
  cazaquistao: 'KZ',
  'asia-central': 'ASC',
  afeganistao: 'AF',
  paquistao: 'PK',
  mongolia: 'MN',
  'coreia-do-norte': 'KP',
  'coreia-do-sul': 'KR',
  japao: 'JP',
  taiwan: 'TW',
  indochina: 'IDC',
  'tailandia-mianmar': 'TH',
  'malasia-singapura': 'MY',
  filipinas: 'PH',
  'nova-zelandia': 'NZ',
  'papua-nova-guine': 'PG',
  'pacifico-sul': 'PSU',
  havai: 'HI',
  'extremo-oriente-russo': 'RU-EO',
  'siberia-ocidental': 'RU-SOC',
  'siberia-oriental': 'RU-SOR',
};

/** Código curto do território (BR-SE, PY, UK...). */
export function code(id: string): string {
  if (COUNTRY_CODES[id]) return COUNTRY_CODES[id]!;
  const parts = id.split('-');
  const prefix: Record<string, string> = {
    brasil: 'BR',
    eua: 'US',
    canada: 'CA',
    mexico: 'MX',
    argentina: 'AR',
    russia: 'RU',
    china: 'CN',
    india: 'IN',
    indonesia: 'ID',
    australia: 'AU',
  };
  const head = prefix[parts[0]!];
  if (head) {
    const rest = parts.slice(1).join('-');
    return `${head}-${REGION_SUFFIX[rest] ?? rest.slice(0, 2).toUpperCase()}`;
  }
  if (SEA_ZONE_BY_ID[id])
    return parts
      .filter((p) => p.length > 2)
      .map((p) => p[0]!.toUpperCase())
      .join('');
  return parts.map((p) => p[0]!.toUpperCase()).join('');
}

const REGION_SUFFIX: Record<string, string> = {
  norte: 'N',
  sul: 'S',
  leste: 'L',
  oeste: 'O',
  sudeste: 'SE',
  nordeste: 'NE',
  noroeste: 'NO',
  sudoeste: 'SO',
  'centro-oeste': 'CO',
  central: 'C',
  ocidental: 'OC',
  oriental: 'OR',
  urais: 'UR',
  'grandes-planicies': 'GP',
  montanhas: 'MT',
  texas: 'TX',
  california: 'CA',
};

export function placeName(view: PlayerView | undefined, id: string): string {
  const t = view?.territories[id];
  if (t?.customName) return t.customName;
  return TERRITORY_BY_ID[id]?.name ?? SEA_ZONE_BY_ID[id]?.name ?? id;
}

export function unitName(id: string): string {
  return UNIT_BY_ID[id]?.name ?? id;
}

export function facilityName(id: string): string {
  if (id === 'stock') return 'Estoque';
  if (id === 'troops') return 'Tropas';
  return FACILITY_BY_ID[id]?.name ?? id;
}

/** "INF38 BLD6 ART4" */
export function unitsLine(units: UnitCounts | undefined): string {
  if (!units) return '';
  const byAbbr = new Map<string, number>();
  for (const [id, q] of Object.entries(units)) {
    if (!q) continue;
    const abbr = UNIT_BY_ID[id]?.abbr ?? id;
    byAbbr.set(abbr, (byAbbr.get(abbr) ?? 0) + q);
  }
  return [...byAbbr.entries()].map(([a, q]) => `${a}${q}`).join(' ');
}

export function bagLine(bag: ResourceBag | undefined): string {
  if (!bag) return '—';
  const parts = Object.entries(bag)
    .filter(([, q]) => (q ?? 0) > 0)
    .map(([r, q]) => `${num(q ?? 0)} ${RESOURCE_ABBR[r as ResourceId]}`);
  return parts.length ? parts.join(' · ') : '—';
}

export const BAND_LABEL: Record<string, string> = {
  none: 'NENHUMA',
  small: 'PEQ',
  medium: 'MÉD',
  large: 'GRD',
  huge: 'ENORME',
};
export const ORDER_ABBR: Record<string, string> = {
  march: 'M',
  defend: 'D',
  support: 'A',
  raid: 'S',
  consolidate: 'C',
};
/** Abreviações das missões e rotas no mapa (padrão do protótipo C2). */
export const MISSION_ABBR: Record<string, string> = {
  bombing: 'BOMB',
  'air-support': 'AP-AÉR',
  'air-patrol': 'PATR',
  'naval-strike': 'ATQ-NAV',
  missile: 'MSL',
  'air-transport': 'TRANSP',
  relocation: 'REALOC',
};
export const ROUTE_ABBR: Record<string, string> = { road: 'ROD', rail: 'FER', air: 'AÉR', sea: 'MAR' };

/** Recursos que uma rota leva (quantidade fixa ou excedente), na ordem do jogo. */
export function routeResources(route: {
  cargo: ResourceBag;
  surplusAbove: ResourceBag | null;
}): ResourceId[] {
  return RESOURCES.filter(
    (r) => (route.cargo[r] ?? 0) > 0 || (route.surplusAbove && route.surplusAbove[r] !== undefined),
  );
}
export const ORDER_NAME: Record<string, string> = {
  march: 'Marcha',
  defend: 'Defesa',
  support: 'Apoio',
  raid: 'Saque',
  consolidate: 'Consolidar',
};

/** Recompensa de um objetivo de governo: "+300 $" ou "+100 AÇO". */
export function rewardText(o: ObjectiveDef): string {
  const parts = [];
  if (o.reward.money) parts.push(`${num(o.reward.money)} $`);
  if (o.reward.resources) parts.push(bagLine(o.reward.resources));
  return `+${parts.join(' · ')}`;
}

/** Resultado de um bombardeio ou míssil no alvo (relatórios de combate e resumo do turno). */
export function strikeText(strike: CombatStrike): string {
  if (strike.intercepted) return 'Míssil abatido antes do impacto.';
  if (strike.nuclear) return 'Detonação nuclear no alvo.';
  if (strike.target === 'troops')
    return strike.killed ? `${num(strike.killed)} baixas nas tropas.` : 'Nenhuma baixa nas tropas.';
  if (strike.target === 'stock')
    return strike.destroyedStock
      ? `${num(strike.destroyedStock)} recursos destruídos no estoque.`
      : 'Nenhum estoque destruído.';
  const name = facilityName(strike.target);
  return strike.destroyedLevels
    ? `${name}: ${strike.destroyedLevels} nível(is) destruído(s).`
    : `${name}: nenhum nível destruído (instalação ausente ou poder de bombardeio insuficiente).`;
}

/** Nome do modo de vitória. */
export const VICTORY_MODE_NAME: Record<VictoryMode, string> = {
  supremacy: 'Supremacia',
  economic: 'Maior potência econômica',
  military: 'Maior potência militar',
  capitals: 'Conquista de capitais',
  control: 'Controle',
  domination: 'Dominação total',
  'nuclear-terror': 'Terror nuclear',
};

/** O que é preciso para vencer (prazo e aliança da partida; metas de balanceamento padrão). */
export function victoryGoal(victory: MatchConfig['victory'], b: Balance = DEFAULT_BALANCE): string {
  const { mode, turnLimit, sharedAlliance } = victory;
  const team = sharedAlliance ? ' (somando a aliança)' : '';
  const hold = `por ${b.victoryHoldTurns} turnos seguidos`;
  switch (mode) {
    case 'domination':
      return `Ser o último governo de pé${sharedAlliance ? ' (ou só restar a sua aliança)' : ''}.`;
    case 'capitals':
      return `Controlar mais de ${Math.round(b.capitalsShare * 100)}% das capitais iniciais${team} ${hold}.`;
    case 'control':
      return `Controlar mais de ${Math.round(b.controlVictoryShare * 100)}% dos territórios terrestres${team} ${hold}.`;
    case 'supremacy':
      return `Ter a maior Pontuação de Poder no turno ${turnLimit}.`;
    case 'economic':
      return `Ter o maior PIB médio no turno ${turnLimit}.`;
    case 'military':
      return `Ter o maior Poder militar no turno ${turnLimit}.`;
    case 'nuclear-terror':
      return 'Ser o primeiro a detonar com sucesso uma arma nuclear em território inimigo.';
  }
}
